import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Paper, CircularProgress, Button, Stack } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { listTenantUsers, updateUser, createUser } from "../../../../services/accountsService";
import UsersHeader from "./UsersHeader";
import UsersListSimple from "./UsersListSimple";
import EditUserDialogSimple from "./EditUserDialogSimple";

export default function TenantUsersSection({ tenantId, disabled = false }) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const loadUsers = useCallback(async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const res = await listTenantUsers({ tenant_id: tenantId, ordering: "username" });
      if (!res.success) throw new Error(res.error || t("tenantUsers.errors.loadFailed", "Failed to load users"));
      setUsers(Array.isArray(res.data) ? res.data : res.data?.results || []);
    } catch (e) {
      enqueueSnackbar(e.message || t("tenantUsers.errors.loadFailed", "Failed to load users"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [tenantId, enqueueSnackbar, t]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await loadUsers();
    } finally {
      setRefreshing(false);
    }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return users;
    return users.filter((up) => {
      const u = up.user || {};
      return (
        (u.username || "").toLowerCase().includes(term) ||
        (u.email || "").toLowerCase().includes(term) ||
        (u.first_name || "").toLowerCase().includes(term) ||
        (u.last_name || "").toLowerCase().includes(term)
      );
    });
  }, [users, q]);

  const openEdit = (up) => {
    setSelected(up);
    setEditOpen(true);
  };
  const openCreate = () => {
    setSelected(null);
    setEditOpen(true);
  };
  const closeEdit = () => setEditOpen(false);

  const saveUser = async (id, data, mode) => {
    const isCreate = mode === "create";
    const fn = isCreate ? createUser : updateUser;

    const res = isCreate ? await fn({ ...data, tenant_id: tenantId }) : await fn(id, data);
    if (!res.success) {
      throw new Error(
        res.error ||
          (isCreate
            ? t("tenantUsers.errors.createFailed", "Create failed")
            : t("tenantUsers.errors.updateFailed", "Update failed"))
      );
    }

    enqueueSnackbar(
      isCreate ? t("tenantUsers.snackbar.created", "User created") : t("tenantUsers.snackbar.updated", "User updated"),
      { variant: "success" }
    );

    await loadUsers();
  };

  if (!tenantId) return null;
  if (loading) return <CircularProgress />;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mb={1}>
        <UsersHeader q={q} setQ={setQ} refreshing={refreshing} onRefresh={handleRefresh} />
        <Button variant="outlined" onClick={openCreate} disabled={disabled}>
          {t("tenantUsers.actions.newUser", "New user")}
        </Button>
      </Stack>

      <Box>
        <UsersListSimple users={filtered} onEdit={openEdit} disabled={disabled} />
      </Box>

      <EditUserDialogSimple
        open={editOpen}
        onClose={closeEdit}
        userProfile={selected}
        disabled={disabled}
        mode={selected ? "edit" : "create"}
        onSave={async (id, data, mode) => {
          await saveUser(id, data, mode);
          closeEdit();
        }}
      />
    </Paper>
  );
}